import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { graphTrpc } from '@org/clients/graph';
import {
  Skeleton,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@org/ui';
import { Trophy, Medal, Award, TrendingUp, Star, Crown } from 'lucide-react';
import { useAuth } from '@org/ui';

export const Route = createFileRoute('/leaderboard')({
  component: LeaderboardPage,
});

function LeaderboardPage() {
  const { t } = useTranslation('community');
  const { user } = useAuth();

  const { data: weeklyData, isLoading: weeklyLoading } = graphTrpc.points.getLeaderboard.useQuery({
    type: 'weekly',
    limit: 10,
  });

  const { data: allTimeData, isLoading: allTimeLoading } = graphTrpc.points.getLeaderboard.useQuery({
    type: 'all-time',
    limit: 50,
  });

  const { data: risingStarsData, isLoading: risingStarsLoading } = graphTrpc.points.getLeaderboard.useQuery({
    type: 'rising-stars',
    limit: 10,
  });

  const { data: myRankData } = graphTrpc.points.getMyRank.useQuery();
  const { data: levelThresholds } = graphTrpc.points.getLevelThresholds.useQuery();

  const levelNameKeys = ['levels.newMember', 'levels.activeMember', 'levels.trustedMember', 'levels.veteran', 'levels.leader'] as const;

  const renderLeaderboardList = (
    data: typeof weeklyData,
    isLoading: boolean,
    showWeeklyPoints = false
  ) => {
    if (isLoading) {
      return (
        <div className="divide-y divide-border rounded-2xl border border-border bg-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="h-6 w-6" />
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      );
    }

    const leaderboard = data?.leaderboard;
    if (!leaderboard || leaderboard.length === 0) {
      return (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <Trophy className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
          <p className="mt-4 text-muted-foreground">{t('leaderboard.noData')}</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-border rounded-2xl border border-border bg-card">
        {leaderboard.map((leaderUser) => {
          const displayName =
            leaderUser.displayName ||
            [leaderUser.firstName, leaderUser.lastName].filter(Boolean).join(' ') ||
            'Anonymous';
          const initial = displayName.slice(0, 1).toUpperCase();
          const rank = leaderUser.rank;
          const isCurrentUser = leaderUser.userId === user?.userId;

          let RankIcon = null;
          let rankColor = 'text-muted-foreground';
          let rankBg = '';
          if (rank === 1) {
            RankIcon = Crown;
            rankColor = 'text-yellow-500';
            rankBg = 'bg-yellow-50';
          } else if (rank === 2) {
            RankIcon = Medal;
            rankColor = 'text-gray-400';
            rankBg = 'bg-muted/50';
          } else if (rank === 3) {
            RankIcon = Award;
            rankColor = 'text-amber-600';
            rankBg = 'bg-amber-50';
          }

          const levelKey = levelNameKeys[leaderUser.communityLevel - 1] ?? levelNameKeys[0];

          return (
            <Link
              key={leaderUser.userId}
              to="/profile/$userId"
              params={{ userId: leaderUser.userId }}
              className={`flex items-center gap-4 p-4 transition-colors hover:bg-muted/50 ${
                isCurrentUser ? 'bg-primary/5' : ''
              } ${rankBg}`}
            >
              <div className={`flex h-8 w-8 items-center justify-center ${rankColor}`} aria-label={`#${rank}`}>
                {RankIcon ? (
                  <RankIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <span className="text-lg font-bold">{rank}</span>
                )}
              </div>
              <Avatar className="h-10 w-10">
                <AvatarImage src={leaderUser.profileImageUrl ?? undefined} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-medium">
                  {displayName}
                  {isCurrentUser && (
                    <span className="ml-2 text-xs text-primary">(You)</span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(levelKey)}
                </p>
              </div>
              <div className="text-right">
                <p className="font-bold text-primary">
                  {showWeeklyPoints && 'weeklyPoints' in leaderUser
                    ? leaderUser.weeklyPoints
                    : leaderUser.points}
                </p>
                <p className="text-xs text-muted-foreground">
                  {showWeeklyPoints ? t('leaderboard.weekly') : t('leaderboard.points')}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  const currentPoints = myRankData?.points ?? 0;
  const currentLevel = myRankData?.level?.level ?? 1;
  const nextLevelAt = myRankData?.level?.nextLevelAt;
  const currentLevelAt = levelThresholds?.levels?.find(
    (l) => l.level === currentLevel
  )?.minPoints ?? 0;

  const progressPercent = nextLevelAt
    ? Math.min(
        100,
        ((currentPoints - currentLevelAt) / (nextLevelAt - currentLevelAt)) * 100
      )
    : 100;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('nav.leaderboard')}</h1>

      {myRankData && (
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Star className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('leaderboard.yourRank')}</p>
                  <p className="text-3xl font-bold">#{myRankData.rank}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
                  <Trophy className="h-7 w-7 text-amber-500" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('leaderboard.totalPoints')}</p>
                  <p className="text-3xl font-bold">{myRankData.points}</p>
                </div>
              </div>

              <div className="flex-1 sm:max-w-xs">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 text-sm">
                  <span className="text-muted-foreground">
                    {t('leaderboard.level')} {currentLevel}: {myRankData.level?.name}
                  </span>
                  {nextLevelAt && (
                    <span className="text-muted-foreground">
                      {t('profile.ptsToNext', { points: nextLevelAt - currentPoints, level: currentLevel + 1 })}
                    </span>
                  )}
                </div>
                <div className="mt-2 h-3 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(progressPercent)} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="weekly">
        <TabsList>
          <TabsTrigger value="weekly">{t('leaderboard.weekly')}</TabsTrigger>
          <TabsTrigger value="all-time">{t('leaderboard.allTime')}</TabsTrigger>
          <TabsTrigger value="rising-stars" className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4" aria-hidden="true" />
            {t('leaderboard.risingStars')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="weekly" className="mt-4">
          <p className="mb-4 text-sm text-muted-foreground">
            {t('leaderboard.weeklyDesc')}
          </p>
          {renderLeaderboardList(weeklyData, weeklyLoading, true)}
        </TabsContent>

        <TabsContent value="all-time" className="mt-4">
          <p className="mb-4 text-sm text-muted-foreground">
            {t('leaderboard.allTimeDesc')}
          </p>
          {renderLeaderboardList(allTimeData, allTimeLoading)}
        </TabsContent>

        <TabsContent value="rising-stars" className="mt-4">
          <p className="mb-4 text-sm text-muted-foreground">
            {t('leaderboard.risingDesc')}
          </p>
          {renderLeaderboardList(risingStarsData, risingStarsLoading)}
        </TabsContent>
      </Tabs>

      {levelThresholds && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('leaderboard.levelProgression')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-5">
              {levelThresholds.levels.map((level) => (
                <div
                  key={level.level}
                  className={`rounded-lg border border-border p-3 text-center ${
                    currentLevel === level.level
                      ? 'border-primary bg-primary/5'
                      : currentLevel > level.level
                      ? 'bg-muted/50'
                      : ''
                  }`}
                >
                  <p className="text-2xl font-bold text-primary">{level.level}</p>
                  <p className="text-sm font-medium">{level.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {level.minPoints}{t('leaderboard.pts')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
